import React, { useState, useEffect, useRef, useCallback } from 'react';
import { conversationsApi, ConversationMessage, SENDER_TYPE } from '../services/conversationsApi';
import { WhatsAppTemplate, Asset } from '../services/marketingApi';
import TemplatePicker from './TemplatePicker';
import TemplateEditor from './TemplateEditor';
import AssetPicker from './AssetPicker';
import MessageBubble from './MessageBubble';
import { AttachmentMenu, AttachmentMenuItemId } from './shared/AttachmentMenu';
import InteractiveMessageComposer from './InteractiveMessageComposer';
import { MediaType } from '../types/asset';
import { format } from 'date-fns';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';

interface WhatsAppChatProps {
  // The conversation to load. The portal never creates one: a conversation is
  // born on the WhatsApp side (the lead writes in, or a template goes out),
  // which is what gives it the phone mapping every send needs.
  conversationId: string;

  // Header display info
  title: string;
  subtitle?: string;

  // Inline = a pane inside a page (Messages); otherwise a slide-over sheet.
  inline?: boolean;
  onClose: () => void;
  onLiveAgentModeChanged?: (conversationId: string, isLiveAgent: boolean) => void;
}

const MAX_TEXTAREA_HEIGHT = 120;

// Helper to create an optimistic message for immediate display
const createOptimisticMessage = (
  conversationId: string,
  text: string,
  extras: Partial<ConversationMessage> = {}
): ConversationMessage => ({
  message_id: `temp-${Date.now()}`,
  conversation_id: conversationId,
  sender_type: SENDER_TYPE.AGENT,
  date_time: new Date().toISOString(),
  text,
  status: 'sending',
  ...extras,
});

const WhatsAppChat: React.FC<WhatsAppChatProps> = ({
  conversationId: initialConversationId,
  title,
  subtitle,
  inline = false,
  onClose,
  onLiveAgentModeChanged,
}) => {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [initializing, setInitializing] = useState(true);
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const skipScrollRef = useRef(false);
  const [conversationId, setConversationId] = useState<string | null>(initialConversationId || null);
  const [showTemplates, setShowTemplates] = useState<false | 'templates' | 'ai-suggestions'>(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inLiveAgentMode, setInLiveAgentMode] = useState(false);
  const [showAgentModeConfirm, setShowAgentModeConfirm] = useState(false);
  const [togglingAgentMode, setTogglingAgentMode] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const agentModeRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback((smooth = true) => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    }
  }, []);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const prevHeight = el.style.height;
    el.style.height = 'auto';
    const newHeight = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT) + 'px';
    el.style.height = newHeight;
    // Only scroll messages into view when textarea height actually changes
    if (prevHeight && prevHeight !== newHeight) {
      setTimeout(() => scrollToBottom(), 0);
    }
  }, [scrollToBottom]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = newMessage;
      const newText = text.substring(0, start) + emojiData.emoji + text.substring(end);
      setNewMessage(newText);
      // Set cursor position after emoji
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emojiData.emoji.length;
        textarea.focus();
      }, 0);
    } else {
      setNewMessage(prev => prev + emojiData.emoji);
    }
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Close agent mode popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (agentModeRef.current && !agentModeRef.current.contains(event.target as Node)) {
        setShowAgentModeConfirm(false);
      }
    };
    if (showAgentModeConfirm) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAgentModeConfirm]);

  const handleToggleAgentMode = async () => {
    if (!conversationId || togglingAgentMode) return;
    setTogglingAgentMode(true);
    try {
      const newMode = !inLiveAgentMode;
      await conversationsApi.updateConversation(conversationId, { in_live_agent_mode: newMode });
      setInLiveAgentMode(newMode);
      onLiveAgentModeChanged?.(conversationId, newMode);
    } catch (err) {
      console.error('Error toggling live agent mode:', err);
    }
    setTogglingAgentMode(false);
    setShowAgentModeConfirm(false);
  };

  const handleRefresh = async () => {
    if (!conversationId || refreshing) return;
    setRefreshing(true);
    try {
      const { messages: updated, has_more } = await conversationsApi.getMessages(conversationId, { limit: 50 });
      setMessages(updated);
      setHasMore(has_more);
    } catch (err) {
      console.error('Error refreshing messages:', err);
    }
    setRefreshing(false);
  };

  // Initialize: Load existing conversation or create new one
  useEffect(() => {
    const initializeConversation = async () => {
      setInitializing(true);
      setError(null);
      try {
        const convId = initialConversationId;
        setConversationId(convId);

        // Fetch messages and conversation metadata in parallel
        const [{ messages: existingMessages, has_more }, convData] = await Promise.all([
          conversationsApi.getMessages(convId, { limit: 50 }),
          conversationsApi.getConversation(convId),
        ]);
        setMessages(existingMessages);
        setHasMore(has_more);
        setInLiveAgentMode(convData.in_live_agent_mode);

        // Mark conversation as read
        conversationsApi.markConversationRead(convId).catch(() => {});
      } catch (err) {
        console.error('Error initializing conversation:', err);
        setError('Failed to load conversation. Please try again.');
      }
      setInitializing(false);
    };

    initializeConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConversationId]);

  useEffect(() => {
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
      return;
    }
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const loadOlderMessages = useCallback(async () => {
    if (!conversationId || loadingMore || !hasMore || messages.length === 0) return;
    setLoadingMore(true);

    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;

    try {
      const oldestDateTime = messages[0].date_time;
      const { messages: olderMsgs, has_more } = await conversationsApi.getMessages(conversationId, {
        limit: 50,
        before: oldestDateTime,
      });
      skipScrollRef.current = true;
      setMessages(prev => [...olderMsgs, ...prev]);
      setHasMore(has_more);

      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - prevScrollHeight;
        }
      });
    } catch (err) {
      console.error('Error loading older messages:', err);
    }
    setLoadingMore(false);
  }, [conversationId, loadingMore, hasMore, messages]);

  // IntersectionObserver to trigger loading older messages on scroll-up
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadOlderMessages();
        }
      },
      { root: messagesContainerRef.current, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadOlderMessages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !conversationId) return;

    const messageText = newMessage.trim();
    setNewMessage('');

    // Reset textarea height immediately
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Create optimistic message and add to UI immediately
    const tempId = `temp-${Date.now()}`;
    const optimisticMsg = createOptimisticMessage(conversationId, messageText);
    optimisticMsg.message_id = tempId;
    setMessages(prev => [...prev, optimisticMsg]);

    try {
      await conversationsApi.sendMessage(conversationId, messageText);
      const { messages: updatedMessages, has_more } = await conversationsApi.getMessages(conversationId, { limit: 50 });
      setMessages(updatedMessages);
      setHasMore(has_more);
    } catch (err) {
      console.error('Error sending message:', err);
      setMessages(prev =>
        prev.map(msg =>
          msg.message_id === tempId ? { ...msg, status: 'failed' as const } : msg
        )
      );
    }
  };

  const handleTemplateSelected = (template: WhatsAppTemplate) => {
    setShowTemplates(false);
    setSelectedTemplate(template);
  };

  const handleSendTemplate = async (template: WhatsAppTemplate) => {
    if (!conversationId) return;

    setSelectedTemplate(null);

    const headerType = (() => {
      switch (template.template_type) {
        case 'IMAGE': return 'IMAGE';
        case 'VIDEO': return 'VIDEO';
        case 'TEXT_HEADER': return 'TEXT';
        default: return undefined;
      }
    })();

    // Create optimistic message for template
    const tempId = `temp-${Date.now()}`;
    const optimisticMsg = createOptimisticMessage(conversationId, template.body, {
      header_type: headerType as 'IMAGE' | 'VIDEO' | 'TEXT' | undefined,
      header_text: template.header_text,
      header_media_url: template.header_media_url,
      footer: template.footer,
      buttons: template.buttons,
      interactive_buttons: template.interactive_buttons,
      carousel_cards: template.carousel_cards,
    });
    optimisticMsg.message_id = tempId;
    setMessages(prev => [...prev, optimisticMsg]);

    try {
      await conversationsApi.sendTemplateMessage(conversationId, {
        text: template.body,
        header_type: headerType,
        header_text: template.header_text,
        header_media_url: template.header_media_url,
        footer: template.footer,
        buttons: template.buttons?.length ? template.buttons : undefined,
        interactive_buttons: template.interactive_buttons?.length ? template.interactive_buttons : undefined,
        carousel_cards: template.carousel_cards?.length ? template.carousel_cards : undefined,
        template_id: template.id,
        template_name: template.name,
      });
      const { messages: updatedMessages, has_more } = await conversationsApi.getMessages(conversationId, { limit: 50 });
      setMessages(updatedMessages);
      setHasMore(has_more);
    } catch (err) {
      console.error('Error sending template:', err);
      setMessages(prev =>
        prev.map(msg =>
          msg.message_id === tempId ? { ...msg, status: 'failed' as const } : msg
        )
      );
    }
  };

  const retryInit = () => {
    setError(null);
    setInitializing(true);
    // Re-trigger the useEffect
    setConversationId(null);
    setTimeout(() => {
      setConversationId(initialConversationId || null);
    }, 0);
  };

  const handleAttachmentSelect = (itemId: AttachmentMenuItemId) => {
    setShowAttachmentMenu(false);
    if (itemId === 'templates' || itemId === 'ai-suggestions') {
      setShowTemplates(itemId);
    } else if (itemId === 'attachments') {
      setShowAssetPicker(true);
    } else if (itemId === 'quick-replies') {
      setShowQuickReplies(true);
    }
  };

  const handleSendQuickReplies = async (
    bodyText: string,
    buttons: { id: string; title: string }[],
    footerText?: string
  ) => {
    if (!conversationId) return;

    setShowQuickReplies(false);

    // Create optimistic message
    const tempId = `temp-${Date.now()}`;
    const optimisticMsg = createOptimisticMessage(conversationId, bodyText, {
      interactive_type: 'button',
      interactive_buttons: buttons,
      footer_text: footerText,
    });
    optimisticMsg.message_id = tempId;
    setMessages(prev => [...prev, optimisticMsg]);

    try {
      await conversationsApi.sendInteractiveMessage(conversationId, bodyText, buttons, footerText);
      const { messages: updatedMessages, has_more } = await conversationsApi.getMessages(conversationId, { limit: 50 });
      setMessages(updatedMessages);
      setHasMore(has_more);
    } catch (err) {
      console.error('Error sending interactive message:', err);
      setMessages(prev =>
        prev.map(msg =>
          msg.message_id === tempId ? { ...msg, status: 'failed' as const } : msg
        )
      );
    }
  };

  const handleAssetSelect = async (asset: Asset, mediaType: MediaType, caption?: string) => {
    if (!conversationId) return;

    setShowAssetPicker(false);

    // Create optimistic message with media
    const tempId = `temp-${Date.now()}`;
    const optimisticMsg = createOptimisticMessage(conversationId, caption || '', {
      header_type: mediaType.toUpperCase() as 'IMAGE' | 'VIDEO' | 'DOCUMENT',
      header_media_url: asset.url,
    });
    optimisticMsg.message_id = tempId;
    setMessages(prev => [...prev, optimisticMsg]);

    try {
      await conversationsApi.sendMediaMessage(conversationId, mediaType, asset.url || '', caption);
      const { messages: updatedMessages, has_more } = await conversationsApi.getMessages(conversationId, { limit: 50 });
      setMessages(updatedMessages);
      setHasMore(has_more);
    } catch (err) {
      console.error('Error sending media message:', err);
      setMessages(prev =>
        prev.map(msg =>
          msg.message_id === tempId ? { ...msg, status: 'failed' as const } : msg
        )
      );
    }
  };

  // Get initial for avatar
  const getInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className={inline ? 'h-full flex flex-col min-w-0' : 'fixed inset-0 z-50 flex justify-end'}>
      {/* Backdrop */}
      {!inline && <div className="absolute inset-0 bg-black/60" onClick={onClose} />}

      {/* Slide-over Panel */}
      <div className={inline
        ? 'relative w-full bg-warm-white flex flex-col h-full min-h-0'
        : 'relative w-full max-w-md bg-warm-white border-l border-sketch flex flex-col h-full animate-slide-in-right'}>
        {/* Header - WhatsApp style */}
        <div className="bg-forest p-4 flex items-center">
          <button onClick={onClose} className={`mr-3 text-warm-white hover:text-warm-white/80 ${inline ? 'hidden' : ''}`}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="w-10 h-10 rounded-full bg-warm-white/20 flex items-center justify-center mr-3">
            <span className="text-warm-white font-medium">
              {getInitial(title)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-warm-white font-medium truncate">{title}</p>
            {subtitle && <p className="text-warm-white/70 text-sm truncate">{subtitle}</p>}
          </div>

          {/* Live Agent Mode Toggle + Refresh */}
          <div className="relative flex-shrink-0 ml-2" ref={agentModeRef}>
            <button
              onClick={() => setShowAgentModeConfirm(prev => !prev)}
              className={`p-2 rounded-full transition-colors ${
                inLiveAgentMode
                  ? 'bg-warm-white/25 text-warm-white'
                  : 'text-warm-white/70 hover:text-warm-white hover:bg-warm-white/10'
              }`}
              title={inLiveAgentMode ? 'Live agent mode active' : 'AI bot mode active'}
            >
              {inLiveAgentMode ? (
                // Person icon — live agent active
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              ) : (
                // Bot/robot icon — AI mode
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7h1a1 1 0 011 1v3a1 1 0 01-1 1h-1v1a2 2 0 01-2 2H5a2 2 0 01-2-2v-1H2a1 1 0 01-1-1v-3a1 1 0 011-1h1a7 7 0 017-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 012-2zM7.5 13A1.5 1.5 0 006 14.5 1.5 1.5 0 007.5 16 1.5 1.5 0 009 14.5 1.5 1.5 0 007.5 13zm9 0a1.5 1.5 0 00-1.5 1.5 1.5 1.5 0 001.5 1.5 1.5 1.5 0 001.5-1.5 1.5 1.5 0 00-1.5-1.5z"/>
                </svg>
              )}
              {inLiveAgentMode && (
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-forest rounded-full border border-white/50" />
              )}
            </button>

            {/* Confirmation Popover */}
            {showAgentModeConfirm && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-warm-white rounded-lg shadow-lg border border-sketch z-50 overflow-hidden">
                <div className="p-3">
                  <p className="text-charcoal text-sm font-medium">
                    {inLiveAgentMode ? 'Switch to AI Mode?' : 'Switch to Live Agent?'}
                  </p>
                  <p className="text-graphite text-xs mt-1">
                    {inLiveAgentMode
                      ? 'Bot will resume responding to messages.'
                      : 'Bot will stop responding. You handle this chat.'}
                  </p>
                </div>
                <div className="flex border-t border-sketch-soft">
                  <button
                    onClick={() => setShowAgentModeConfirm(false)}
                    className="flex-1 px-3 py-2 text-sm text-graphite hover:bg-cream transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleToggleAgentMode}
                    disabled={togglingAgentMode}
                    className="flex-1 px-3 py-2 text-sm font-medium text-forest hover:bg-forest/10 transition-colors border-l border-sketch-soft disabled:opacity-50"
                  >
                    {togglingAgentMode ? 'Switching...' : 'Switch'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={refreshing || initializing}
            className="flex-shrink-0 p-2 text-warm-white/70 hover:text-warm-white hover:bg-warm-white/10 rounded-full transition-colors disabled:opacity-40"
            title="Refresh messages"
          >
            <svg className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {/* Messages Area */}
        <div ref={messagesContainerRef} className="flex-1 overflow-auto p-4 bg-[#e5ddd5]" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23d4ccc4\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }}>
          {/* Loading State */}
          {initializing ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-full bg-forest/20 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-forest animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <p className="text-graphite mb-2">
                {initialConversationId ? 'Loading conversation...' : 'Starting conversation...'}
              </p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-full bg-brick/10 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-brick" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-brick mb-2">{error}</p>
              <button
                onClick={retryInit}
                className="text-forest text-sm underline"
              >
                Retry
              </button>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-full bg-forest/20 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-forest" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <p className="text-graphite mb-2">No messages yet</p>
              <p className="text-graphite text-sm">Send a message to start the conversation</p>
            </div>
          ) : (
            <div>
              <div ref={topSentinelRef} className="h-1" />
              {loadingMore && (
                <div className="flex justify-center py-3">
                  <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-forest" />
                </div>
              )}
              {messages.map((msg) => (
                <MessageBubble key={msg.message_id} message={msg} />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Templates Panel - Now using TemplatePicker component */}
        {showTemplates && (
          <div className="absolute bottom-20 left-4 right-4 z-10">
            <TemplatePicker
              conversationId={showTemplates === 'ai-suggestions' ? conversationId || undefined : undefined}
              onTemplateSelected={handleTemplateSelected}
              onClose={() => setShowTemplates(false)}
            />
          </div>
        )}

        {/* Input Area - WhatsApp style */}
        <div className="bg-[#f0f0f0] px-2 py-1 flex items-end gap-1">
          {/* Attachment Menu ("+") */}
          <AttachmentMenu
            isOpen={showAttachmentMenu}
            onToggle={() => setShowAttachmentMenu(prev => !prev)}
            onItemSelect={handleAttachmentSelect}
            disabled={initializing || !conversationId}
          />

          {/* Text input container */}
          <div className="flex-1 flex items-end bg-warm-white rounded-[21px] px-2 min-h-[42px] relative">
            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div
                ref={emojiPickerRef}
                className="absolute bottom-full left-0 mb-2 z-50"
              >
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  theme={Theme.LIGHT}
                  width={300}
                  height={350}
                  searchPlaceholder="Search emoji..."
                  previewConfig={{ showPreview: false }}
                />
              </div>
            )}

            {/* Emoji icon */}
            <button
              className={`flex-shrink-0 p-1 ${showEmojiPicker ? 'text-[#00a884]' : 'text-[#8696a0]'}`}
              type="button"
              onClick={() => setShowEmojiPicker(prev => !prev)}
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9.153 11.603c.795 0 1.439-.879 1.439-1.962s-.644-1.962-1.439-1.962-1.439.879-1.439 1.962.644 1.962 1.439 1.962zm5.694 0c.795 0 1.439-.879 1.439-1.962s-.644-1.962-1.439-1.962-1.439.879-1.439 1.962.644 1.962 1.439 1.962zM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10zm5.009-7.672c-.354-.751-1.528-1.194-2.697-.834-.612.189-1.386.455-2.312.455-.927 0-1.7-.266-2.312-.455-1.169-.36-2.343.083-2.697.834-.187.397-.048.861.327 1.074.375.213.851.088 1.074-.249.075-.113.397-.154.823-.018.574.183 1.393.413 2.785.413s2.211-.23 2.785-.413c.426-.136.748-.095.823.018.223.337.699.462 1.074.249.375-.213.514-.677.327-1.074z"/>
              </svg>
            </button>

            {/* Auto-expanding textarea */}
            <textarea
              ref={textareaRef}
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                autoResize();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder={initializing ? 'Loading...' : 'Type a message'}
              disabled={initializing || sending || !conversationId}
              rows={1}
              className="flex-1 py-2 px-1 text-[15px] text-charcoal placeholder-[#8696a0] focus:outline-none disabled:opacity-50 resize-none leading-[1.4] bg-transparent"
              style={{ maxHeight: MAX_TEXTAREA_HEIGHT }}
            />
          </div>

          {/* Send button - WhatsApp green circle */}
          <button
            onClick={handleSendMessage}
            disabled={initializing || sending || !newMessage.trim() || !conversationId}
            className="w-[42px] h-[42px] bg-[#00a884] rounded-full flex items-center justify-center text-warm-white hover:bg-[#008f72] transition-colors disabled:opacity-40 flex-shrink-0"
          >
            {sending ? (
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
                <path d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Template Editor Modal */}
      {selectedTemplate && (
        <TemplateEditor
          template={selectedTemplate}
          onSend={handleSendTemplate}
          onCancel={() => setSelectedTemplate(null)}
        />
      )}

      {/* Asset Picker Modal */}
      <AssetPicker
        isOpen={showAssetPicker}
        onSelect={handleAssetSelect}
        onClose={() => setShowAssetPicker(false)}
      />

      {/* Quick Replies Composer Modal */}
      {showQuickReplies && (
        <InteractiveMessageComposer
          onSend={handleSendQuickReplies}
          onCancel={() => setShowQuickReplies(false)}
          sending={sending}
        />
      )}
    </div>
  );
};

export default WhatsAppChat;
